<template>
  <v-main
    data-testid="dashboard-view"
    scrollable
    class="dashboard-container"
  >
    <v-container
      class="pa-0"
      fluid
      style="max-height: 100vh"
    >
      <v-row
        class="dashboard-row header-row pt-4 pb-2"
        style="background-color:#FEE0B3"
      >
        <v-col
          cols="12"
          class="pa-0"
        >
          <v-tabs
            v-model="tab"
            selected-class="active-tab"
            class="d-flex dashboard-header"
          >
            <div class="dashboard-header__item d-flex ml-11">
              <div>
                <LabsIcon />
              </div>
              <h2 class="dashboard-header__item__title">
                Beyond Maps - Shared
              </h2>
            </div>
            <div class="dashboard-header__item">
              <v-tab
                value="user"
                color="#3b3b3b"
                data-testid="user-tab"
              >
                My Maps
              </v-tab>
              <v-tab
                value="org"
                color="#3b3b3b"
                data-testid="org-tab"
              >
                Organization Maps
              </v-tab>
            </div>
            <div class="dashboard-header__item dashboard-header__btn">
              <np-action-btn
                class="mr-10"
                :href="beyondMapsUrl()"
              >
                To Beyond Maps
              </np-action-btn>
            </div>
          </v-tabs>
        </v-col>
      </v-row>
      <v-row class="dashboard-row dashboard-search-row">
        <v-col
          cols="12"
          class="px-0 "
        >
          <div class="map-search">
            <np-action-btn
              rounded="xl"
              prepend-icon="mdi-plus"
              height="45"
              size="large"
              :disabled="loading"
              @click="newMap"
            >
              Create a Map
            </np-action-btn>
            <MapSearchBar
              v-model="searchTerm"
              @clear-search="clearSearch"
              @update-search-term="updateSearchTerm"
              @on-keydown="onKeydown"
            />
            <div />
          </div>
          <h2
            v-if="resultFound === false"
            class="mt-4"
          >
            No results found
          </h2>
        </v-col>
      </v-row>
      <v-row class="dashboard-row dashboard-body-row">
        <v-col
          cols="12"
          class="px-0 "
        >
          <v-window v-model="tab">
            <v-window-item value="user">
              <v-progress-circular
                v-if="loading"
                :size="70"
                :width="7"
                indeterminate
                color="primary"
              />
              <v-row
                v-else
                style="padding-bottom: 4px;"
              >
                <MapCard
                  v-for="map in mapsToRenderUser"
                  :key="map.id"
                  :map="map"
                  :data-testid="map.id"
                  @delete="deleteMap(map.id)"
                  @duplicate="handleDuplicateMap(map.id)"
                />
              </v-row>
            </v-window-item>
            <v-window-item value="org">
              <v-progress-circular
                v-if="loading"
                :size="70"
                :width="7"
                indeterminate
                color="primary"
              />
              <v-row v-else>
                <MapCard
                  v-for="map in mapsToRenderOrg"
                  :key="map.id"
                  :map="map"
                  @delete="deleteMap(map.id)"
                  @duplicate="handleDuplicateMap(map.id)"
                />
              </v-row>
            </v-window-item>
          </v-window>
        </v-col>
      </v-row>
      <v-row
        v-if="!loading"
        class="dashboard-row"
      >
        <v-col
          cols="12"
          class="px-0"
        >
          <v-pagination
            v-model="page"
            :length="pageCount"
          />
        </v-col>
      </v-row>
    </v-container>
  </v-main>
  <DeleteSharedMap
    v-if="showDeletePopup"
    :map-id="deleteMapId"
    @close="onDeletePopupClose"
  />
</template>

<script>
// External dependencies
import {mapMutations, mapState} from 'vuex';

// Libs and Services
import {createNewSharedMap, duplicateSharedMap} from '@/lib/classes/SharedMap';
import {currentUser} from '@/services/FirestoreService';
import {getMapsForPageNumber} from '@/services/AlgoliaService';
import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {connectHits, connectSearchBox} from 'instantsearch.js/es/connectors';

// Components
import MapCard from '@/external/components/MapCard.vue';
import DeleteSharedMap from '@/components/DeleteSharedMap.vue';
import LabsIcon from '@/components/svgs/LabsIcon.vue';
import MapSearchBar from '@/external/components/MapSearchBar.vue';
import {createAuditRecord} from '@/services/BackendService';
import {TokenService} from '@/services/TokenService';

export default {
  name: 'DashboardView',
  components: {
    MapCard,
    DeleteSharedMap,
    LabsIcon,
    MapSearchBar,
  },
  data() {
    const userMaps = [];
    const allMaps = [];
    const recentlyAdded = [];
    return {
      searchTerm: '',
      searching: false,
      resultFound: true,
      userMaps,
      allMaps,
      recentlyAdded,
      timeout: null,
      // Extract the tab name from the URL hash to initialize the correct tab
      tab: window.location.hash.substring(1),
      page: 1,
      pageCount: 1,
      currentUser: currentUser(),
      searchResults: [],
      showDeletePopup: false,
      deleteMapId: null,
      loading: true,
      refineSearchCallback: null,
      labsLogo: 'https://storage.googleapis.com/beyond_maps_public/Icons_Browser/Labs-Icon.png',
    };
  },
  computed: {
    ...mapState(['recentlyDeletedMapIds']),
    // Combine all results to keep a parent level active index
    useProdGCP() {
      return process.env.VUE_APP_GCP_ENV === 'production';
    },
    mapsToRenderOrg() {
      // If we don't yet have a value for the current page, return an empty array
      if (!this.allMaps[this.page-1]) return [];
      const maps = this.searching === false ? this.allMaps[this.page-1] : this.searchResults;
      // In the case that a recently added map is deleted, remove it from the recently added list
      const filteredRecentlyAdded = this.recentlyAdded.filter((map) => !this.recentlyDeletedMapIds.includes(map.id));
      return [...filteredRecentlyAdded, ...maps];
    },
    mapsToRenderUser() {
      // If we don't yet have a value for the current page, return an empty array
      if (!this.userMaps[this.page-1]) return [];
      const maps = this.searching === false ?
        this.userMaps[this.page-1] :
          this.searchResults.filter((result) => result.createdOn.uid === this.currentUser.uid);

      // In the case that a recently added map is deleted, remove it from the recently added list
      const filteredRecentlyAdded = this.recentlyAdded.filter((map) => !this.recentlyDeletedMapIds.includes(map.id));

      // This is to ensure that the recently duplicated maps are rendered immediately as Algolia syncs with Firestore
      return [...filteredRecentlyAdded, ...maps];
    },
  },
  watch: {
    async tab() {
      this.page = 1;
      this.clearSearch();
      window.location.hash = this.tab;
      await this.updateMaps();
    },
    async page() {
      await this.updateMaps();
    },
    recentlyDeletedMapIds() {
      this.userMaps[this.page-1] = this.userMaps[this.page-1].filter((map) =>
        !this.recentlyDeletedMapIds.includes(map.id));
      this.allMaps[this.page-1] = this.allMaps[this.page-1].filter((map) =>
        !this.recentlyDeletedMapIds.includes(map.id));
    },
  },
  mounted() {
    document.title = 'Shareable Maps';
    // Initialize algolia
    this.initializeAlgolia();
    this.initializeMaps();
  },
  methods: {
    ...mapMutations(['setSharedMap']),
    async initializeMaps() {
      // Both tabs load at once, and each logs its own failure.
      this.loadFirstMapsPage(this.allMaps, 'org');
      this.loadFirstMapsPage(this.userMaps, 'user', this.currentUser.uid);
    },
    async loadFirstMapsPage(maps, tab, userId) {
      try {
        const response = await getMapsForPageNumber(0, userId);
        maps[0] = response.hits;
        maps[this.page-1] = maps[this.page-1].filter((map) => !this.recentlyDeletedMapIds.includes(map.id));
        if (this.tab === tab) {
          this.pageCount = response.nbPages;
          this.loading = false;
        }
      } catch (error) {
        console.error(error);
      }
    },
    clearSearch() {
      this.searchTerm = '';
      this.searching = false;
      this.resultFound = true;
    },
    beyondMapsUrl() {
      if (process.env.NODE_ENV === 'development') {
        return 'https://labs.northpointkc.com/beta/maps';
      } else {
        return 'https://labs.northpointkc.com/maps';
      }
    },
    async updateMaps() {
      if (this.tab === 'org') {
        const allMapsResponse = await getMapsForPageNumber(this.page-1);
        this.allMaps[this.page-1] = allMapsResponse.hits;
        this.pageCount = allMapsResponse.nbPages;
      } else {
        const userMapsResponse = await getMapsForPageNumber(this.page-1, this.currentUser.uid);
        this.userMaps[this.page-1] = userMapsResponse.hits;
        this.pageCount = userMapsResponse.nbPages;
      }
    },
    async newMap() {
      const sharedMap = await createNewSharedMap();
      this.setSharedMap(sharedMap);
      createAuditRecord('createdOn');
      this.$router.push({
        name: 'ExternalMapView',
        params: {
          id: sharedMap.id,
          mode: 'edit',
        },
      });
    },
    deleteMap(id) {
      this.showDeletePopup = true;
      this.deleteMapId = id;
    },
    async handleDuplicateMap(id) {
      // Adds tile to dashboard for immediate rendering
      // Features are not loaded yet so the tile is disabled
      this.recentlyAdded.push({
        loading: true,
        editedOn: {
          name: currentUser().displayName,
          timestamp: Date.now(),
        }});

      const data = await duplicateSharedMap(id);
      const duplicateMapId = data.duplicateId;
      const duplicateMapData = data.duplicatedMapData;

      // The old disabled tile is removed and the new enabled tile is added
      this.recentlyAdded.pop();
      this.recentlyAdded.push({id: duplicateMapId, ...duplicateMapData});
    },
    async onDeletePopupClose(isDelete) {
      this.showDeletePopup = false;
      // if the user clicks cancel, stay on the current tab
      if (isDelete) {
        this.tab = 'user';
      }
      await this.updateMaps();
    },
    onKeydown(event) {
      if (event.code === 'Backspace' && this.searchTerm.length === 0) {
        event.preventDefault();
        return;
      }
    },
    updateSearchTerm() {
      if (this.searchTerm === '') {
        this.clearSearch();
        this.timeout = null;
      } else {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          if (this.searchTerm != '') {
            this.searching= true;
          }
          if (!this.refineSearchCallback) return;
          this.refineSearchCallback(this.searchTerm);
        }, 300);
      }
    },
    /**
     * Function for initializing algolia indices and components
     */
    async initializeAlgolia() {
      let key;
      try {
        key = await TokenService.getToken('algoliaKey');
      } catch (error) {
        console.error(new Error(`Failed to retrieve Algolia key: ${error}`));
      }
      // ALGOLIA SEARCH
      const search = instantsearch({
        searchClient: algoliasearch('1TIGF75UT0', key),
        indexName: process.env.VUE_APP_ALGOLIA_INDEX ?? 'dev_shareableMaps',
        future: {
          preserveSharedStateOnUnmount: true,
        },
      });

      const renderSearchBox = (renderOptions, isFirstRender) => {
        if (isFirstRender) {
          this.refineSearchCallback = renderOptions.refine;
        }
      };

      // create custom widgets w/ callbacks
      const customSearchBox = connectSearchBox(renderSearchBox);
      const hits = connectHits(this.renderHits);
      // Instantiate the custom search and other widgets
      search.addWidgets([
        customSearchBox({
          container: document.querySelector('#mapSearchBox'),
        }),
        hits({
          container: document.querySelector('#hits'),
        }),
      ]);
      search.start();
    },
    /**
     * Function for rendering algolia firestore hits
     * @param {Object} renderOptions
     * @param {boolean} isFirstRender
     */
    renderHits(renderOptions, isFirstRender) {
      if (!isFirstRender && this.searchTerm) {
        this.resultFound = renderOptions.hits.length > 0;
        this.searchResults = renderOptions.hits;
        this.pageCount = 1;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.dashboard-container {
  font-family: main.$fontPrimary;
  background: main.$lightGray;
}
.dashboard-header {
  height: 100%;
  justify-content: space-between;
  &__item {
    width: 33%;

    @media screen and (max-width: 750px) {
      margin: 0px !important;
      padding: 0px !important;
      width: 100%;
      align-items: center;
      justify-content: center;
    }

    &__title {
      display: flex;
      align-items: center;
      margin-left: 6px;
      @media screen and (max-width: 990px) {
        font-size: 20px;
      }
    }
  }
  &__btn {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    @media screen and (max-width: 750px) {
      justify-content: center;
      margin: 0px !important;
      :deep(.v-btn) {
        margin: 10px 0 0 0 !important;
      }
    }
  }
}
.dashboard-row {
  max-height: 100%;
  color: #3b3b3b;
  :deep(.v-slide-group__content) {
    align-items: center;
    display: flex;
    @media screen and (max-width: 750px) {
      flex-direction: column;
      justify-content: space-evenly;
    }
  }
}
.dashboard-search-row {
  margin-left: 44px;
  margin-right: 8px;
  @include main.custom-margin(1470, 10, 50);
  @include main.custom-margin(1510, 10, 24);
  @include main.custom-margin(1605, 10, 42);
  @include main.custom-margin(1728, 10, 54);
  @include main.custom-margin(1800, 10, 30);
  @include main.custom-margin(1915, 10, 40);
}
.dashboard-body-row {
  margin-left: 44px;
  margin-right: 8px;
  @media(min-width: 1470px) {
    margin: 10px 24px;
  }
}

// this is the class passed to the v-tabs component
// eslint-disable-next-line vue-scoped-css/no-unused-selector
.active-tab {
  background-color: rgb(255 255 255 / 20%);
}
.map-search {
  display: flex;
  width: 100%;
  justify-content: space-between;
}
:deep(.v-main__scroller) {
  overflow-x: hidden;
}
</style>
